import { FilterOptions } from '@totem/components/common/filterAutoComplete/FilterAutoCompleteTypes';
import { Optimize } from '@totem/components/ticketing/optimizeTypes';
import {
  Attribute,
  ConnectionInput,
  ForeignKey,
  Note,
  ObjectReference,
  PagingResults,
} from '@totem/types/common';

export const MIN_DATE_TIME_STRING: string = '0001-01-01T00:00:00.000+00:00';

export interface TicketingFilterOptions {
  type: FilterOptions;
  impact: FilterOptions;
  urgency: FilterOptions;
  priority: FilterOptions;
  status: FilterOptions;
  subStatus: FilterOptions;
  categories: FilterOptions;
  building: FilterOptions;
  organization: FilterOptions;
  device: FilterOptions;
}

export type TicketingConnectionInput = ConnectionInput<TicketingFilters>;

export type TicketingFilters = {
  ids?: string[];
  regionId?: string[];
  buildingId?: string[];
  controlSystemId?: string[];
  deviceId?: string[];
  queueId?: string[];
  type?: string[];
  status?: string[];
  category?: string[];
  subCategory?: string[];
  impact?: string[];
  urgency?: string[];
  priority?: string[];
  reference?: string[];
  ticketNumber?: string;
  favorites?: string[];
};

export interface TicketQueryResults {
  tickets: TicketQueryResult[];
  paging: PagingResults;
}

export interface TicketQueryResult {
  ticket: Ticket;
  organization: ObjectReference;
  region: ObjectReference;
  building: ObjectReference;
  controlSystem: ObjectReference;
  device: ObjectReference;
  relatedTickets: RelatedTicket[];
}

export interface RelatedTicket {
  relationship: string;
  ticket: TicketQueryResult;
}

export interface RelatedTicketReference {
  relationship: string;
  objectType: string;
  id: string;
  name: string;
}

export interface Ticket {
  id: string;
  number: string;
  organizationId: string;
  regionId: string;
  buildingId: string;
  controlSystemId: string;
  deviceId: string;
  type: string;
  category: string;
  subCategory: string;
  status: string;
  subStatus: string;
  impact: string;
  urgency: string;
  priority: string;
  summary: string;
  description: string;
  activity: Activity[];
  partiesInvolved: PartyInvolved[];
  queueAssignments: QueueAssignment[];
  assignments: Assignment[];
  createdByType: string;
  createdByName: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string;
  closedAt: string;
  keys: ForeignKey[];
  attributes: TicketAttribute[];
  actualHours: number;
  tasks: Tasks;
  timeEntries: TimeEntries;
  billingEntries: BillingEntries;
  project: Project;
  followUpAt: string;
  internalOnly: boolean;
  approvalRequests: ApprovalRequests;
  extras?: Extras;
  relatedTickets?: RelatedTicketReference[];
  messages?: TicketMessage[];
  favorites: string[];
}

export interface TicketMessage {
  objectReference: ObjectReference;
  templateId: string;
  internalMessageID: string;
  xMessageID: string;
  recipients: TicketMessageRecipient[];
  activities: TicketMessageActivity[];
}

export interface TicketMessageRecipient {
  type: string;
  addressType: string;
  address: string;
}

export interface TicketMessageActivity {
  activity: string;
  timestamp: string;
}

export interface TicketAttribute {
  source: string;
  name: string;
  dataType: string;
  value: string;
}

export interface Activity {
  id: string;
  type: ActivityType;
  notes: Note;
  attributes: Attribute[];
  createdByType: string;
  createdByName: string;
  createdById: string;
  createdAt: string;
  isPrivate: boolean;
  objectReference?: ObjectReference;
  keys: ForeignKey[];
}

export interface TicketAISummaryResult {
  summary: string;
  currentStatus: string;
  keyPoints: string[];
  nextSteps: string[];
}

export interface ActivityType {
  id: string;
  code: string;
  display: string;
}

export interface PartyInvolved {
  id: string;
  type: string;
  status: string;
  notify: boolean;
  name: string;
  email: string;
  phone: string;
  involvementStart: string;
  involvementEnd: string;
  createdByType: string;
  createdByName: string;
  createdById: string;
  keys: ForeignKey[];
}

export interface QueueAssignment {
  id: string;
  queueId: string;
  status: string;
  name: string;
  startTime: string;
  endTime: string;
  createdByType: string;
  createdByName: string;
  createdById: string;
  keys: ForeignKey[];
}

export interface Assignment {
  id: string;
  status: string;
  name: string;
  email: string;
  startTime: string;
  endTime: string;
  createdByType: string;
  createdByName: string;
  createdById: string;
  keys: ForeignKey[];
}

export interface ActivityConfiguration {
  id: string;
  code: string;
  display: string;
  createRequirements: ActivityCreateRequirements;
  viewRequirements: ActivityViewRequirements;
  extendedFieldGroups: ActivityExtendedFieldGroup[];
}

export interface ActivityCreateRequirements {
  internalOnly: boolean;
  roles: number[];
  securityGroups: string[];
  supportedTicketTypes: string[];
  statuses: string[];
  subStatuses: string[];
}

export interface ActivityViewRequirements {
  roles: number[];
  securityGroups: string[];
}

export interface ActivityExtendedFieldGroup {
  name: string;
  display: string;
  extendedFields: ActivityExtendedField[];
}

export interface ActivityExtendedField {
  name: string;
  label: string;
  dataType: string;
  isRequired: boolean;
  display: boolean;
  options: ActivityExtendedFieldOption[];
}

export interface ActivityExtendedFieldOption {
  display: string;
  value: string;
}

export interface ExtendedFieldValue {
  key: string;
  name: string;
  label: string;
  value: string;
  isValid: boolean;
  display: boolean;
  id: string;
}

export interface TicketCategory {
  name: string;
  children?: TicketCategory[];
  createRequirements?: TicketTypeCreateRequirements;
}

export interface TicketTypeConfig {
  name: string;
  description: string;
  prefix: string;
  abbreviation: string;
  style: TicketTypeStyle;
  createRequirements: TicketTypeCreateRequirements;
}

export interface TicketTypeStyle {
  primaryColor: string;
}

export interface TicketTypeCreateRequirements {
  internalOnly: boolean;
  roles: number[];
  securityGroups: string[];
  organizations?: string[];
}

export interface TicketAgingValue {
  label: string;
  value: number;
}

export const TASK_TYPE_GROUP = 'Group';
export const TASK_TYPE_TASK = 'Task';
export const TASK_TYPE_MILESTONE = 'Milestone';

export type Tasks = Task[];

export interface Task {
  id: string;
  type: string;
  number: number;
  parent: number;
  position: number;
  dependencies: TaskDependency[];
  name: string;
  description: string;
  labels: string[];
  expectedStartDate?: string;
  expectedDuration: Duration;
  actualStartDate?: string;
  actualDuration: Duration;
  actualEndDate?: string;
  dueDate?: string;
  assignedResources: string[];
  status: string;
  budgetedHours: number;
  actualHours: number;
  billingType: string;
  isBillingMilestone: boolean;
  isDeleted: boolean;
  createdByType: string;
  createdByName: string;
  createdById: string;
  createdAt: string;
  internalOnly: boolean;
}

export interface Duration {
  days: number;
  hours: number;
  minutes: number;
}

export interface HierarchicalTask extends Task {
  children?: HierarchicalTask[]; // Optional array of child tasks
}

export interface TaskTotals {
  budgetedHours: number;
  actualHours: number;
  expectedDuration: Duration;
}

export interface TaskDependency {
  taskId: number;
  relationshipType: string;
}

export type TimeEntries = TimeEntry[];

export interface TimeEntry {
  id: string;
  taskId: string;
  resourceId: string;
  date: string;
  hoursLogged: number;
  billable: boolean;
  description: string;
  billingId: string;
  createdByType: string;
  createdByName: string;
  createdById: string;
  createdAt: string;
  isDeleted: boolean;
}

export interface Project {
  startDate: string;
  endDate: string;
  status: string;
  fee: number;
  billedAmount: number;
  budgetedHours: number;
  actualHours: number;
  billingType: string;
  expensesType: string;
}

export interface Extras {
  optimize?: Optimize;
  expenses: Expense[];
  overlayNetwork?: OverlayNetwork;
  customerInfo: CustomerInfo;
  technicianInfo: TechnicianInfo;
}

// Mirrors ticketing-library/models/extras.go CustomerInfo/TechnicianInfo/Cause/Action --
// no omitempty on the Go side, so these are always present (zero-value strings/empty
// arrays on tickets created before this existed), not optional.
export interface CustomerInfo {
  whatItMeans: string;
  whyTicketRaised: string;
  impact: string;
}

export interface TechnicianInfo {
  likelyCauses: Cause[];
  actions: Action[];
}

export interface Cause {
  sequence: number;
  message: string;
}

export interface Action {
  sequence: number;
  message: string;
}

export interface OverlayNetwork {
  overlayNetworkId: string;
  overlayNetworkName: string;
  overlayNetworkStatus: string;
  startTime: string;
  expectedEndTime: string;
}

export interface Expense {
  id: string;
  category: string;
  currency: string;
  amount: number;
  exchangeRate: number;
  billable: boolean;
  expenseDate: string;
  description: string;
  createdBy: string;
  createdAt: string;
}

export type Resources = Resource[];

export interface Resource {
  id: string;
  name: string;
  email: string;
}

export type BillingEntries = Billing[];

export interface Billing {
  id: string;
  taskId: string;
  status: string; // READY_TO_BILL, BILLED, PAYED
  amount: number;
  date: string;
  description: string;
  timeEntryIds: string[];
  createdByType: string;
  createdByName: string;
  createdById: string;
  createdAt: string;
}

export type TaskWorkflowSteps = TaskWorkflowStep[];

export interface TaskWorkflowStep {
  code: string;
  name: string;
  description: string;
  options: string[];
}

export interface BillingGetRequest {
  resourceEmails: string[];
  ticketIds: string[];
}

export interface UnbilledTimeEntry {
  id: string;
  ticket: TicketReference;
  organization: ObjectReference;
  region: ObjectReference;
  building: ObjectReference;
  controlSystem: ObjectReference;
  task: Task;
  timeEntry: TimeEntry;
  resource: Resource;
}

export interface TicketReference {
  id: string;
  number: string;
  summary: string;
  salesOrder: string;
  salesOrderLine: string;
  billingMethodology: string;
}

export interface BillingEntry {
  id: string;
  organizationId: string;
  billingId: string;
  timeEntryIds: string[];
  createdByType: string;
  createdByName: string;
  createdById: string;
  createdAt: string;
}

export interface BilledEntry {
  billingId: string;
  createdBy: string;
  createdAt: string;
  organization: ObjectReference;
  items: BilledEntryItem[];
}

export interface BilledEntryItem {
  region: ObjectReference;
  building: ObjectReference;
  controlSystem: ObjectReference;
  resource: Resource;
  task: Task;
  timeEntry: TimeEntry;
  ticket: TicketReference;
}

export interface TaskPositionUpdate {
  id: string;
  parent: number;
  position: number;
}

export interface TaskPositionUpdateRequest {
  ticketId: string;
  updates: TaskPositionUpdate[];
}

export type MyTasks = MyTask[];

export interface MyTask {
  ticket: MyTaskTicket;
  tasks: Tasks;
}

export interface MyTaskTicket {
  id: string;
  number: string;
  summary: string;
}

export interface MyTaskExtended extends Task {
  ticket: MyTaskTicket;
}

export interface ObjectRelationship {
  relationship: string;
  objectType: string;
  id: string;
  name: string;
}

export interface ApprovalRequest {
  id: string;
  approvalGroup: ApprovalGroup;
  labels: string[];
  summary: string;
  description: string;
  status: 'PENDING' | 'IN PROGRESS' | 'APPROVED' | 'REJECTED' | 'SENT';
  objectReference: ObjectRelationship;
  extras?: string; // Omitempty is handled by making the property optional
  createdByType: string;
  createdByName: string;
  createdById: string;
  createdAt: string;
}

// Represents a collection of ApprovalRequest
export type ApprovalRequests = ApprovalRequest[];

export interface ApprovalGroup {
  id: string;
  operator: 'AND' | 'OR';
  status: 'PENDING' | 'SENT' | 'APPROVED' | 'REJECTED';
  outcomeAt: string;
  approvals: Approvals;
  groups: ApprovalGroups;
}

// Represents a collection of ApprovalGroup
export type ApprovalGroups = ApprovalGroup[];

export interface Approval {
  id: string;
  approverEmail: string;
  approverPartyInvolvedId: string;
  status: 'PENDING' | 'SENT' | 'APPROVED' | 'REJECTED';
  reason: string;
  outcomeAt: string;
  requestedAt: string;
}

// Represents a collection of Approval
export type Approvals = Approval[];

export interface SelectedApprovals {
  approvalRequest: ApprovalRequest;
  approval: Approval;
}

export type LinkResponse = {
  token: string;
};

export interface OptimizeDeferralRequest {
  deferUntil: string;
  notes?: string;
}
